"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { SearchBar } from "@/components/search/search-bar";
import type { SearchItem } from "@/lib/search";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/directory", label: "Directory" },
  { href: "/clubs", label: "Clubs" },
  { href: "/events", label: "Events" },
  { href: "/books-films", label: "Books & Films" },
  { href: "/rowing-machines", label: "Rowing Machines" },
  { href: "/blog", label: "Blog" },
  { href: "/submit", label: "Submit a Site" },
];

interface HeaderProps {
  searchItems?: SearchItem[];
}

export function Header({ searchItems }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
        <Link href="/" className="flex items-center gap-2">
          <Image
            src="/images/branding/logo.png"
            alt="rowrowrow.blog"
            width={200}
            height={40}
            priority
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light"
            >
              {link.label}
            </Link>
          ))}
          {searchItems && <SearchBar items={searchItems} />}
        </nav>

        {/* Mobile: search + menu button */}
        <div className="flex items-center gap-3 md:hidden">
          {searchItems && <SearchBar items={searchItems} />}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="border-t border-gray-200 px-4 py-3 md:hidden dark:border-gray-800">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="block py-2 text-sm font-medium text-gray-600 hover:text-primary dark:text-gray-300"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
